import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
  ChangeChannelRequest,
  ChannelEndpoint,
  QuotationExchangeChannel,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  createChannelRequest,
  emailIsValid,
  errorMessage,
  selectableChannels,
} from './quotationExchange.utils'

const endpointKey = (endpoint?: ChannelEndpoint) =>
  endpoint ? `${endpoint.channel}:${endpoint.address || ''}` : ''

const endpointLabel = (endpoint: ChannelEndpoint) =>
  endpoint.displayName
    ? `${endpoint.displayName} (${endpoint.address || 'no address'})`
    : endpoint.address || 'No address'

const withCurrentEndpoint = (
  options: ChannelEndpoint[],
  current?: ChannelEndpoint,
) =>
  current &&
  !options.some((option) => endpointKey(option) === endpointKey(current))
    ? [current, ...options]
    : options

export function ExchangeChannelDialog({
  open,
  title,
  partyId,
  channel: initialChannel,
  sender: initialSender,
  recipient: initialRecipient,
  recipientOptions = [],
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  partyId?: string
  channel?: QuotationExchangeChannel
  sender?: ChannelEndpoint
  recipient?: ChannelEndpoint
  recipientOptions?: ChannelEndpoint[]
  onClose: () => void
  onSave: (request: ChangeChannelRequest) => Promise<void>
}) {
  const [channel, setChannel] = useState<QuotationExchangeChannel>(
    QuotationExchangeChannel.Scsn,
  )
  const [sender, setSender] = useState<ChannelEndpoint>()
  const [recipient, setRecipient] = useState<ChannelEndpoint>()
  const [senderOptions, setSenderOptions] = useState<ChannelEndpoint[]>([])
  const [loadingSenders, setLoadingSenders] = useState(false)
  const [error, setError] = useState<string>()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setChannel(initialChannel || QuotationExchangeChannel.Scsn)
    setSender(initialSender)
    setRecipient(initialRecipient)
    setSenderOptions([])
    setError(undefined)
  }, [initialChannel, initialRecipient, initialSender, open])

  useEffect(() => {
    if (!open || channel !== QuotationExchangeChannel.Email || !partyId)
      return undefined

    let active = true
    setLoadingSenders(true)
    import('./quotationExchange.compatibility')
      .then(({ QuotationExchangeCompatibilityClient }) =>
        new QuotationExchangeCompatibilityClient(partyId).emailSenders(),
      )
      .then((endpoints) => {
        if (!active) return
        setSenderOptions(
          endpoints.filter(
            (endpoint) =>
              endpoint.channel === QuotationExchangeChannel.Email &&
              endpoint.isEnabled !== false &&
              emailIsValid(endpoint.address),
          ),
        )
      })
      .catch((requestError) => {
        if (active) setError(errorMessage(requestError))
      })
      .finally(() => {
        if (active) setLoadingSenders(false)
      })

    return () => {
      active = false
    }
  }, [channel, open, partyId])

  const save = async () => {
    try {
      setSaving(true)
      setError(undefined)
      await onSave(createChannelRequest(channel, sender, recipient))
      onClose()
    } catch (requestError) {
      setError(errorMessage(requestError))
    } finally {
      setSaving(false)
    }
  }

  const manual = channel === QuotationExchangeChannel.Manual
  const email = channel === QuotationExchangeChannel.Email
  const availableSenders = withCurrentEndpoint(
    senderOptions,
    sender?.channel === QuotationExchangeChannel.Email ? sender : undefined,
  )
  const availableRecipients = withCurrentEndpoint(
    recipientOptions.filter(
      (endpoint) =>
        endpoint.channel === QuotationExchangeChannel.Email &&
        emailIsValid(endpoint.address),
    ),
    recipient?.channel === QuotationExchangeChannel.Email
      ? recipient
      : undefined,
  )
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <FormControl fullWidth>
            <InputLabel>Channel</InputLabel>
            <Select
              label="Channel"
              value={channel}
              onChange={(event) => {
                const nextChannel = event.target
                  .value as QuotationExchangeChannel
                setChannel(nextChannel)
                if (nextChannel !== QuotationExchangeChannel.Email) return
                setSender(
                  initialSender?.channel === nextChannel
                    ? initialSender
                    : undefined,
                )
                setRecipient(
                  initialRecipient?.channel === nextChannel
                    ? initialRecipient
                    : undefined,
                )
              }}
            >
              {selectableChannels.map((value) => (
                <MenuItem key={value} value={value}>
                  {value === QuotationExchangeChannel.Scsn ? 'SCSN' : value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {email && (
            <>
              <FormControl
                fullWidth
                required
                error={!emailIsValid(sender?.address)}
              >
                <InputLabel id="exchange-email-sender-label">Sender</InputLabel>
                <Select
                  labelId="exchange-email-sender-label"
                  label="Sender"
                  value={endpointKey(sender)}
                  disabled={loadingSenders}
                  onChange={(event) =>
                    setSender(
                      availableSenders.find(
                        (endpoint) =>
                          endpointKey(endpoint) === event.target.value,
                      ),
                    )
                  }
                >
                  {availableSenders.map((endpoint) => (
                    <MenuItem
                      key={endpointKey(endpoint)}
                      value={endpointKey(endpoint)}
                    >
                      {endpointLabel(endpoint)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                required
                error={!emailIsValid(recipient?.address)}
              >
                <InputLabel id="exchange-email-recipient-label">
                  Recipient
                </InputLabel>
                <Select
                  labelId="exchange-email-recipient-label"
                  label="Recipient"
                  value={endpointKey(recipient)}
                  onChange={(event) =>
                    setRecipient(
                      availableRecipients.find(
                        (endpoint) =>
                          endpointKey(endpoint) === event.target.value,
                      ),
                    )
                  }
                >
                  {availableRecipients.map((endpoint) => (
                    <MenuItem
                      key={endpointKey(endpoint)}
                      value={endpointKey(endpoint)}
                    >
                      {endpointLabel(endpoint)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <Alert severity="info">
            {manual
              ? 'Manual exchange generates a document for download.'
              : email
                ? 'Email requires an enabled party sender and an explicit supplier recipient.'
                : 'SCSN sender and recipient endpoints are resolved automatically.'}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            saving ||
            loadingSenders ||
            (email &&
              (!emailIsValid(sender?.address) ||
                !emailIsValid(recipient?.address)))
          }
          loading={saving}
          onClick={save}
        >
          Save channel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
